import * as amplitude from '@amplitude/analytics-browser'
import { plugin as engagementPlugin } from '@amplitude/engagement-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { configureAmplitudeEngagementRouter } from '@/core/config/amplitude-engagement.util'
import router from '@/core/config/router.installer'
import { PRIORITIES } from '@/core/constants/installation-priorities.constant'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import {
  getAnalyticsContextSnapshot,
  syncAnalyticsBaseContext,
} from '@/shared/analytics'

export const priority = PRIORITIES.AMPLITUDE

let isAmplitudeInitialized = false

const INVALID_API_KEYS = new Set([
  '',
  '0',
  'null',
  'undefined',
  'your_amplitude_api_key_here',
])

function hasValidApiKey(apiKey) {
  return typeof apiKey === 'string' && !INVALID_API_KEYS.has(apiKey.trim().toLowerCase())
}

export function install({ app }) {
  if (isAmplitudeInitialized)
    return

  const apiKey = (import.meta.env.VITE_AMPLITUDE_KEY || '').trim()

  if (!hasValidApiKey(apiKey)) {
    console.warn('[Amplitude] API key not configured - analytics disabled')
    return
  }

  const environment = import.meta.env.VITE_AMPLITUDE_ENV || import.meta.env.MODE
  const sampleRate = Number.parseFloat(import.meta.env.VITE_AMPLITUDE_SESSION_SAMPLE_RATE) || 0.5
  const serverZone = import.meta.env.VITE_AMPLITUDE_SERVER_ZONE || 'US'

  // Validate serverZone
  if (!['US', 'EU'].includes(serverZone)) {
    console.warn(`[Amplitude] Invalid serverZone "${serverZone}", defaulting to "US"`)
  }

  amplitude.init(apiKey, {
    environment,
    serverZone,
    fetchRemoteConfig: true,
    autocapture: {
      attribution: true, // Track traffic attribution
      fileDownloads: true,
      formInteractions: true,
      pageViews: true,
      sessions: true,
      elementInteractions: true, // Auto-track clicks (Jira requirement: EE-131)
      networkTracking: true, // Track API calls performance
      webVitals: true, // Track Core Web Vitals (LCP, FID, CLS)
      frustrationInteractions: true, // Track rage clicks, dead clicks
    },
    trackingOptions: {
      ipAddress: true, // GDPR compliance - don't track IP addresses
    },
  })

  let authStore = null
  try {
    authStore = useAuthStore()
  }
  catch (error) {
    console.warn('[Amplitude] Auth store unavailable for event enrichment:', error)
  }
  const enrichmentPlugin = {
    name: 'enrich-all-events',
    type: 'enrichment',
    execute: async (event) => {
      syncAnalyticsBaseContext({
        route: router.currentRoute.value,
        authStore,
      })

      event.event_properties = {
        ...getAnalyticsContextSnapshot(),
        ...event.event_properties,
      }

      return event
    },
  }

  amplitude.add(enrichmentPlugin)

  // Add session replay plugin
  amplitude.add(sessionReplayPlugin({
    sampleRate,
    privacyConfig: {
      defaultMaskLevel: 'medium', // Mask all form inputs for privacy
      blockSelector: [
        '[data-sensitive]', // Custom attribute for sensitive fields
        '[type="password"]', // Password fields
        '[autocomplete="cc-number"]', // Credit card numbers
        '[autocomplete="cc-csc"]', // Credit card CVV
      ],
    },
  }))

  amplitude.add(engagementPlugin({ serverZone }))
  configureAmplitudeEngagementRouter(router)

  // Provide instance for composable
  app.provide('amplitude', amplitude)
  isAmplitudeInitialized = true
}
